import { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const C = {
  green:   "#059669",
  greenBg: "#ECFDF5",
  dark:    "#111827",
  medium:  "#374151",
  light:   "#6B7280",
  border:  "#E5E7EB",
  white:   "#FFFFFF",
  red:     "#DC2626",
  redBg:   "#FEF2F2",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
};

function ZenviqLogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width={36} height={36} viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill={C.green}/>
        <polyline points="10,11 30,11 10,29 30,29" stroke="white" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <polyline points="9,36 15,32 22,34 31,28" stroke="rgba(255,255,255,0.45)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
      <span style={{ fontSize: 22, fontWeight: 800, color: C.dark, letterSpacing: "-0.04em", fontFamily: "'Space Grotesk', sans-serif" }}>
        Zenviq
      </span>
    </div>
  );
}

// ── Paso único: Ingresá con Google ────────────────────────────────────────────
function PasoLogin({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const handleGoogle = async (cred) => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API_URL}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: cred.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al iniciar sesión");
      localStorage.setItem("token", data.token);
      onLogin(data.usuario);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔐</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: C.dark, margin: "0 0 8px", letterSpacing: "-0.03em" }}>
        Ingresá con Google
      </h2>
      <p style={{ fontSize: 14, color: C.light, margin: "0 0 28px", lineHeight: 1.6 }}>
        Usá la misma cuenta de Google con la que compraste el plan Pro.
        Tu acceso se activará automáticamente.
      </p>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, height: 48, color: C.light }}>
          <div style={{ width: 20, height: 20, border: `2px solid ${C.border}`, borderTop: `2px solid ${C.green}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }}/>
          <span style={{ fontSize: 14 }}>Verificando tu plan...</span>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <GoogleLogin
            onSuccess={handleGoogle}
            onError={() => setError("Error con Google. Intentá nuevamente.")}
            text="continue_with"
            shape="rectangular"
            size="large"
            width="300"
          />
        </div>
      )}

      {error && (
        <div style={{ marginTop: 14, padding: "10px 14px", background: C.redBg, border: `1px solid #FECACA`, borderRadius: 8, fontSize: 13, color: C.red }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Paso: plan no encontrado (fallback) ───────────────────────────────────────
function PasoSinPlan({ nombre }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: C.amber, margin: "0 0 8px", letterSpacing: "-0.03em" }}>
        Hola, {nombre.split(" ")[0]}
      </h2>
      <p style={{ fontSize: 14, color: C.medium, margin: "0 0 16px", lineHeight: 1.6 }}>
        Iniciaste sesión correctamente pero no encontramos tu plan Pro asociado a esta cuenta.
      </p>
      <div style={{ padding: "12px 16px", background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 10, fontSize: 13, color: C.amber, textAlign: "left", lineHeight: 1.7 }}>
        <strong>¿Qué puede haber pasado?</strong><br/>
        • Compraste con un email diferente al que acabás de usar.<br/>
        • El pago todavía está siendo procesado (puede demorar 1-2 min).<br/>
        • Hubo un problema con el pago.
      </div>
      <p style={{ fontSize: 13, color: C.light, marginTop: 16 }}>
        Escribinos a{" "}
        <a href="mailto:info@zenviq.app" style={{ color: C.green, textDecoration: "none", fontWeight: 600 }}>
          info@zenviq.app
        </a>{" "}
        y te lo resolvemos en minutos.
      </p>
    </div>
  );
}

// ── Paso: éxito ───────────────────────────────────────────────────────────────
function PasoExito({ nombre }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: C.green, margin: "0 0 8px" }}>
        ¡Bienvenido al Plan Pro, {nombre.split(" ")[0]}!
      </h2>
      <p style={{ fontSize: 14, color: C.light, margin: 0 }}>
        Redirigiendo a tu panel...
      </p>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function ActivacionPro({ onActivado }) {
  const [usuario, setUsuario] = useState(null);
  const [paso,    setPaso]    = useState("login"); // login | exito | sin-plan

  const handleLogin = (u) => {
    setUsuario(u);
    const esPro = u.plan === "pro" || u.rol === "admin";
    if (esPro) {
      setPaso("exito");
      setTimeout(() => onActivado(u), 2000);
    } else {
      setPaso("sin-plan");
    }
  };

  // Pasos visuales según el estado actual
  const pasos = [
    { label: "Pago",    completado: true,            actual: false },
    { label: "Ingreso", completado: paso !== "login", actual: paso === "login" },
    { label: "Acceso",  completado: paso === "exito", actual: paso !== "login" && paso !== "exito" },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(160deg, ${C.white} 60%, ${C.greenBg} 100%)`,
      display: "flex", flexDirection: "column",
      fontFamily: "'Space Grotesk', -apple-system, sans-serif",
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ padding: "clamp(16px, 3vw, 28px) clamp(1rem, 5%, 7%)", borderBottom: `1px solid ${C.border}`, background: C.white }}>
        <ZenviqLogo />
      </div>

      {/* Contenido centrado */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(24px, 5vw, 48px) 1rem" }}>
        <div style={{ width: "100%", maxWidth: 440 }}>

          {/* Badge de pago confirmado */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: C.greenBg, border: `1px solid rgba(5,150,105,0.25)`,
            borderRadius: 20, padding: "6px 16px", marginBottom: 28,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, display: "inline-block" }}/>
            <span style={{ fontSize: 13, color: C.green, fontWeight: 700 }}>Pago confirmado</span>
          </div>

          {/* Indicador de pasos */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
            {pasos.map(({ label, completado, actual }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, flex: i < pasos.length - 1 ? "none" : 1 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                  background: completado ? C.green : actual ? "#EEF2FF" : C.border,
                  color: completado ? C.white : actual ? "#4338CA" : "#9CA3AF",
                  border: actual ? "2px solid #4338CA" : "none",
                }}>
                  {completado ? "✓" : i + 1}
                </div>
                <span style={{ fontSize: 12, fontWeight: actual ? 700 : 400, color: actual ? C.dark : C.light, whiteSpace: "nowrap" }}>
                  {label}
                </span>
                {i < pasos.length - 1 && (
                  <div style={{ flex: 1, height: 1, background: completado ? C.green : C.border, minWidth: 20 }}/>
                )}
              </div>
            ))}
          </div>

          {/* Card principal */}
          <div style={{
            background: C.white, borderRadius: 20,
            padding: "clamp(24px, 5vw, 36px) clamp(20px, 5vw, 36px)",
            boxShadow: "0 8px 40px rgba(0,0,0,0.09)", border: `1px solid ${C.border}`,
          }}>
            {paso === "login"    && <PasoLogin    onLogin={handleLogin} />}
            {paso === "exito"    && <PasoExito    nombre={usuario?.nombre || ""} />}
            {paso === "sin-plan" && <PasoSinPlan  nombre={usuario?.nombre || ""} />}
          </div>

          {/* Footer */}
          <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 20 }}>
            ¿Problemas? Escribinos a{" "}
            <a href="mailto:info@zenviq.app" style={{ color: C.green, textDecoration: "none" }}>
              info@zenviq.app
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
